/**
 * Returns an array of 12 localized month names (e.g., ["January", "February", ...]).
 * Uses Intl.DateTimeFormat to respect locale conventions.
 */
export const getMonthNames = (locale: string): string[] => {
  const formatter = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) => {
    const date = new Date(Date.UTC(2023, i, 1));
    return formatter.format(date);
  });
};

export const getWeekStart = (locale: string): number => {
  const normalized = locale.toLowerCase();
  const sundayLocales = ['en-us', 'en-ca', 'fr-ca'];
  const saturdayLocales = ['ar-ae', 'ar-sa'];

  if (sundayLocales.includes(normalized)) return 0;
  if (saturdayLocales.includes(normalized)) return 6;
  return 1; // default to Monday
};

export const getWeekdayLabels = (locale: string, weekStartsOn: number): string[] => {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  return Array.from({ length: 7 }, (_, i) => {
    const day = (i + weekStartsOn) % 7;
    const date = new Date(Date.UTC(2023, 0, 1 + day));
    return formatter.format(date);
  });
};

export const getLocalizedDatePlaceholder = (locale: string): string => {
  const formatter = new Intl.DateTimeFormat(locale);
  const parts = formatter.formatToParts(new Date(2000, 0, 31));
  return parts
    .map((part) => {
      if (part.type === 'day') return 'DD';
      if (part.type === 'month') return 'MM';
      if (part.type === 'year') return 'YYYY';
      return part.value;
    })
    .join('');
};

/**
 * Returns a normalized date string (YYYY-MM-DD) using UTC timezone.
 * Uses UTC date components to avoid timezone shift issues (e.g., UTC midnight
 * shifting to the previous day when converted to local time in Western timezones).
 */
export const getNormalizedDate = (date: Date | string | number): string => {
  const d = new Date(date);
  const year = d.getUTCFullYear();
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseDateInput = (dateInput: Date | string | number): Date => {
  if (dateInput instanceof Date) {
    return new Date(dateInput.getTime());
  }

  if (typeof dateInput === 'string') {
    const trimmed = dateInput.trim();

    // Handle ISO date-only format (YYYY-MM-DD) explicitly as UTC to avoid browser inconsistencies
    // Some browsers parse "2026-02-10" as local midnight, others as UTC midnight
    const isoDateOnly = /^(\d{4})-(\d{2})-(\d{2})$/;
    const isoMatch = trimmed.match(isoDateOnly);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      return new Date(Date.UTC(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10)));
    }

    const normalizedTimezoneSpacing = trimmed
      .replace(/\s+Z$/i, 'Z')
      .replace(/\s+([+-]\d{2}:?\d{2})$/, '$1');

    const normalized = normalizedTimezoneSpacing.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
    const parsed = new Date(normalized);

    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }

    return new Date(trimmed);
  }

  return new Date(dateInput);
};
